import { Category } from '@repo/types';
import c from 'classnames';
import { Badge, Typography } from 'components';
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useCategorySectionEvaluations from 'services/kits/useCategorySectionEvaluations';
import useKitSummaryScores from 'services/kits/useKitSummaryScores';
import {
  calculateNavigationBadge,
  categoryAnchorId,
  countCategoryEvaluations,
  getProNavigationBadge,
  getSectionEvaluationBadge,
  PRO_CATEGORY_NAME,
} from 'views/Results/utils/categoriesAndMetrics';
import { RowData } from './CategoriesList';
import styles from './MetricsNavigation.module.css';

type NavigationRowProps = {
  category: Category;
  isActive: boolean;
  onClick?: (category: Category) => void;
  isDropdown?: boolean;
  isDropdownSelectedOption?: boolean;
  setRowData?: React.Dispatch<React.SetStateAction<RowData>>;
};

export default function NavigationRow({
  category,
  isActive,
  onClick,
  isDropdown = false,
  isDropdownSelectedOption = false,
  setRowData,
}: NavigationRowProps) {
  const { kitId } = useParams<{ kitId: string }>();
  const isPro = category.name === PRO_CATEGORY_NAME;

  // Only PRO rows fetch pro_mss; disabled (null kitId) for every other category.
  const { data: proSummaryScores } = useKitSummaryScores(
    isPro ? (kitId ?? null) : null,
    'pro_mss',
  );

  const { red, orange, ...rest } = useMemo(
    () => countCategoryEvaluations(category),
    [category],
  );

  const categoryRowRef = useRef<HTMLAnchorElement | null>(null);

  useLayoutEffect(() => {
    if (isActive && categoryRowRef.current && setRowData) {
      const top =
        categoryRowRef.current.offsetTop +
        categoryRowRef.current.clientHeight * 0.1;
      const height = `${categoryRowRef.current.clientHeight * 0.8}px`;
      setRowData({ top, height });
    }
  }, [isActive]);

  const supportLabel = 'support';
  const midLabel = isPro ? 'borderline' : 'improve';

  const proBadge = isPro ? getProNavigationBadge(proSummaryScores?.[0]) : null;

  const sectionEvaluations = useCategorySectionEvaluations(kitId);
  const sectionBadge = isPro
    ? null
    : getSectionEvaluationBadge(
        sectionEvaluations.get(category.name)?.section_evaluation,
      );

  const [badgeTitle, badgeState] =
    proBadge ??
    sectionBadge ??
    calculateNavigationBadge({ red, orange, ...rest }, isPro);

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    onClick?.(category);

    if (!isDropdownSelectedOption)
      document
        .getElementById(categoryAnchorId(category))
        ?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <a
      href={`#${categoryAnchorId(category)}`}
      className={c(
        styles.categoryRow,
        isActive && styles.active,
        isDropdown && styles['isDropdown'],
      )}
      onClick={handleClick}
      ref={categoryRowRef}
    >
      <div className={styles.textsContainer}>
        <div className={styles.categoryNameContainer}>
          <Typography
            variant='body-s'
            className={styles.categoryName}
            color='dark'
          >
            {category.name}
          </Typography>
          {!isDropdown && <Badge title={badgeTitle} state={badgeState} />}
        </div>

        <div className={styles.evaluationsCount}>
          {red > 0 && (
            <Typography color='red' variant='caption'>
              {red} {supportLabel}
              {orange > 0 && ','}
            </Typography>
          )}
          {orange > 0 && (
            <Typography color='orange' variant='caption'>
              {orange} {midLabel}
            </Typography>
          )}
        </div>
      </div>
      {isDropdown && <Badge title={badgeTitle} state={badgeState} />}
    </a>
  );
}
